import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  EVENT_MODAL_STATE_LOGIN_CLOSE,
  EVENT_MODAL_STATE_LOGIN_OPEN,
} from '@/hooks/useAuthModalState';
import {
  loginUser,
  resetPassword,
  resetUser,
  selectAuthResetPasswordError,
  selectAuthResetPasswordLoading,
} from '@/redux/modules/auth';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { selectModalState } from '@/redux/modules/modal/selectors';
import { TRootState } from '@/redux/rootReducer';
import { ILoginForm } from '@/services/types/auth/ILoginForm';
import { getReCaptchaToken } from '@/utility/recaptcha';
import {
  EVENT_NAME_CLOSE_LOGIN,
  EVENT_NAME_LOGIN,
  EVENT_NAME_LOGOUT,
  registerAuthenticateEvent,
} from '@/utility/session';
import { triggerSignupModal } from '@/utility/triggerSignupModal';

import { ForgotPasswordFormFields } from './ForgotPasswordForm';
import LoginModal from './LoginModal';

type TAuth = TRootState['auth'];

const LoginModalContainer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosable, setIsClosable] = useState(true);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectAuthenticated);
  const modalState = useSelector(selectModalState);
  const authState = useSelector<TRootState, TAuth>(state => state.auth);
  const resetPasswordLoading = useSelector(selectAuthResetPasswordLoading);
  const resetPasswordError = useSelector(selectAuthResetPasswordError);

  const onLogin = useCallback((data: ILoginForm) => dispatch(loginUser(data)), [dispatch]);
  const onLogout = useCallback(() => dispatch(resetUser()), [dispatch]);
  const onResetPassword = useCallback(
    (formData: ForgotPasswordFormFields) => dispatch(resetPassword({ email: formData.email })),
    [dispatch],
  );

  let email;

  if (modalState.id === 'login') {
    email = modalState.props?.email;
  }

  const onClose = useCallback(() => {
    setIsOpen(false);
    // Dispatch modal state event for popover tracking
    document.dispatchEvent(new CustomEvent(EVENT_MODAL_STATE_LOGIN_CLOSE));
  }, [setIsOpen]);

  // Track modal open state changes
  useEffect(() => {
    if (isOpen) {
      document.dispatchEvent(new CustomEvent(EVENT_MODAL_STATE_LOGIN_OPEN));
    } else {
      document.dispatchEvent(new CustomEvent(EVENT_MODAL_STATE_LOGIN_CLOSE));
    }
  }, [isOpen]);

  useEffect(() => {
    registerAuthenticateEvent(EVENT_NAME_CLOSE_LOGIN, onClose); // TODO: Do this via redux post R1
    registerAuthenticateEvent(EVENT_NAME_LOGOUT, onLogout, true);
    registerAuthenticateEvent(EVENT_NAME_LOGIN, (event?: CustomEvent) => {
      // if the user logs out in a different tab, update the redux store to indicate they are logged out
      if (!isAuthenticated) {
        onLogout();
        setIsOpen(true);
      }

      setIsClosable(event?.detail?.closable ?? true);
    });
  }, [onClose, onLogout, isAuthenticated]);

  useEffect(() => {
    if (!isOpen && isResettingPassword) {
      setTimeout(() => {
        setIsResettingPassword(false);
      }, 1000);
    }
  }, [isOpen, isResettingPassword]);

  const onSignup = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    triggerSignupModal(isClosable);
  };

  return (
    <LoginModal
      isAuthenticated={isAuthenticated}
      isOpen={isOpen}
      isClosable={isClosable}
      isResettingPassword={isResettingPassword}
      onForgotPassword={() => setIsResettingPassword(!isResettingPassword)}
      onSignup={onSignup}
      onClose={onClose}
      loginFormProps={{
        onLogin,
        email,
        isLoading: authState.isLoggingIn,
        error: authState.error,
        reCaptchaToken: getReCaptchaToken(),
      }}
      resetPasswordFormProps={{
        loading: resetPasswordLoading,
        error: resetPasswordError,
        onSubmit: onResetPassword,
      }}
    />
  );
};

export default LoginModalContainer;
